import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { YtgService } from "@modules/ytg/api/ytg.service";
import type { Operation } from "@shared-types";

/** Return shape of {@link useYtgOperation}. */
export interface UseYtgOperationReturn {
    data: Operation | undefined;
    loading: boolean;
    error: Error | null;
    /** Re-run the operation query. */
    refetch: UseQueryResult<Operation>["refetch"];
}

/**
 * Query hook returning a single YTG operation by id (disabled when id is nullish).
 *
 * @example
 * ```tsx
 * const { data, loading } = useYtgOperation(operationId);
 * ```
 */
export const useYtgOperation = (id: number | string | null | undefined): UseYtgOperationReturn => {
    const { data, isLoading, error, refetch } = useQuery<Operation>({
        queryKey: ["ytg", "operation", id],
        queryFn: ({ signal }) => YtgService.getOperationById(id!, { signal }),
        enabled: Boolean(id),
    });
    return { data, loading: isLoading, error, refetch };
};
